import { Injectable, Inject, Optional, signal, computed } from '@angular/core';
import { combineLatest, map, Observable, catchError, timeout, EMPTY, Subject, switchMap } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { latLng, LatLng, latLngBounds } from 'leaflet';
import { XMLParser } from 'fast-xml-parser';
import { ConnectionService } from '../../services/connection.service';
import { WmsFieldMappingHandler, WMS_FIELD_MAPPING_HANDLER } from '../handlers';
import { OfflineModeService } from '../../services/offline-mode.service';


const mergeWith = (o1: any, o2: any) => {
	const o: any = {};
	for (const key of [...Object.keys(o1), ...Object.keys(o2)])
		o[key] = o1[key] ? o1[key] : o2[key];
	return o;
}

@Injectable({
	providedIn: 'root'
})
export class WmsDataService {

	// MV WMS-Server
	private fgkMVurl = 'https://www.geodaten-mv.de/dienste/forst_grundkarte_wms';
	private fsMVurl = 'https://www.geodaten-mv.de/dienste/forst_struktur_wms';
	private gemarkungenMVurl = 'https://www.geodaten-mv.de/dienste/dfg_wms';
	private flurstueckMVurl = 'https://www.geodaten-mv.de/dienste/inspire_cp_alkis_view';
	private standortkarteMVurl = 'https://www.geodaten-mv.de/dienste/forst_sok_wms';

	// BB WMS/WFS-Server
	private fsBBurl = 'https://www.brandenburg-forst.de/geoserver/fs/wms';
	private standortkarteBBurl = 'https://www.brandenburg-forst.de/geoserver/stok/wms';
	private katasterBBurl = 'https://isk.geobasis-bb.de/ows/alkis_vereinf_wfs';

	// Deutschlandweit
	private wuchsgebieteD = 'https://inspire.thuenen.de/geoserver/wgwb/ows';

	private isOnline = false;
	private wmsAvailable = true;
	private lastWmsCheck = 0;
	private readonly WMS_CHECK_INTERVAL = 60000; // 1 Minute zwischen Checks

	// Rate-Limiting: Max. gleichzeitige Anfragen und Queue
	private readonly MAX_CONCURRENT_REQUESTS = 3;
	private readonly REQUEST_DELAY_MS = 50; // Verzögerung zwischen Anfragen
	private activeRequests = 0;
	private requestQueue: (() => void)[] = [];
	private lastRequestTime = 0;

	// Signal für WMS-Verfügbarkeit (für UI-Anzeige)
	private _wmsUnavailable = signal(false);

	/**
	 * Gibt an, ob WMS-Daten nicht abgerufen werden können.
	 * True wenn: Offline-Modus aktiv ODER WMS-Abruf fehlgeschlagen
	 */
	wmsUnavailable = computed(() => this.oms.offlineMode() || this._wmsUnavailable());

	/**
	 * Set von Feldnamen, für die bereits eine erfolgreiche WMS-Abfrage stattfand.
	 * Auch wenn die Antwort leer war (z.B. Punkt außerhalb Wald), gilt das Feld als "abgefragt".
	 */
	private _queriedFields = signal<Set<string>>(new Set());

	/**
	 * Prüft ob ein Feld bereits erfolgreich per WMS abgefragt wurde.
	 * Wenn ja, soll keine Warnung angezeigt werden (auch wenn leer).
	 */
	isFieldQueried(fieldName: string): boolean {
		return this._queriedFields().has(fieldName);
	}

	/**
	 * Markiert Felder als erfolgreich abgefragt (auch wenn Antwort leer war).
	 */
	private markFieldsQueried(fields: string[]): void {
		const current = this._queriedFields();
		const updated = new Set(current);
		fields.forEach(f => updated.add(f));
		this._queriedFields.set(updated);
	}

	/**
	 * Setzt den Query-Status zurück (z.B. bei neuem Punkt oder Koordinatenänderung).
	 */
	resetQueriedFields(): void {
		this._queriedFields.set(new Set());
	}

	/** Liste der Felder, die per WMS automatisch befüllt werden */
	static readonly WMS_FIELDS = [
		'Forstamt', 'Revier',
		'Abteilung', 'Unterabteilung', 'Teilfläche', 'Hilfsfläche',
		'Gemarkung', 'Flur', 'Flurstück',
		'Klimastufe', 'KlimastufeNeu',  // Typeahead-Control fügt 'Neu' an den Namen an
		'Wuchsgebiet', 'Wuchsbezirk'
	];

	/** Event wenn WMS-Daten geladen wurden (für View-Aktualisierung) */
	readonly wmsDataLoaded = new Subject<void>();

	constructor(
		private cs: ConnectionService,
		private http: HttpClient,
		private oms: OfflineModeService,
		@Optional() @Inject(WMS_FIELD_MAPPING_HANDLER) private fieldMappingHandler?: WmsFieldMappingHandler<any>
	) {
		cs.monitor().subscribe((isOnline: boolean) => { this.isOnline = isOnline; })
	}
	
	getWMSFeatureData(baseUrl: string, layer: string, c: LatLng, format = 'text/plain'): Observable<string> {
		const tol = 0.00005;
		const bbox = latLngBounds(latLng(c.lat - tol, c.lng - tol), latLng(c.lat + tol, c.lng + tol)).toBBoxString(); // BBOX in EPSG:4326
		const params = new HttpParams({
			fromObject: {
				SERVICE: 'WMS',
				VERSION: '1.1.1',          // keep 1.1.1 to avoid axis‑order headaches
				REQUEST: 'GetFeatureInfo',
				LAYERS: layer,
				QUERY_LAYERS: layer,
				STYLES: '',
				BBOX: bbox,
				SRS: 'EPSG:4326',          // use 'SRS' in 1.1.1 (not 'CRS')
				WIDTH: '101',
				HEIGHT: '101',
				X: '50',
				Y: '50',
				INFO_FORMAT: format,
				FEATURE_COUNT: '1'
			}
		});
    return this.http
      .get(baseUrl, { params, responseType: 'arraybuffer', observe: 'response' }).pipe(
        map(response => {
          const contentType = response.headers.get('Content-Type') || '';
          const charsetMatch = contentType.match(/charset=([^\s;]+)/i);
          const encoding = charsetMatch ? charsetMatch[1] : 'iso-8859-1';
          return new TextDecoder(encoding).decode(response.body!);
        })
      );
	}
	
	getValues(keys: string[], data: string): any {
		const result: any = {};
		keys.forEach(key => {
			const rx = new RegExp(`${key}\\s*=\\s*(?:'([^']*)'|([^\\n]+))`);
			const match = data.match(rx);
			result[key] = match ? (match[1] ? match[1] : match[2]) : '';
		});
		return result;
	}
	
	getXMLValues(keys: string[], data: string): any {
		function findKeyRecursive(obj: any, targetKey: string) {
			if (typeof obj !== 'object' || obj === null) return undefined;

			if (targetKey in obj) return obj[targetKey];

			for (const key in obj) {
				const value = obj[key];
				if (typeof value === 'object') {
					const result: any = findKeyRecursive(value, targetKey);
					if (result !== undefined) return result;
				}
			}

			return undefined;
		}
		
		const parser = new XMLParser({
			ignoreAttributes: false,       // keep @attrs
			attributeNamePrefix: '@',
		});
		const json = parser.parse(data);
		const result: any = {};
		keys.forEach(key => {
			const value = findKeyRecursive(json, key);
			if (value !== undefined) {
				result[key] = value;
			}
		});
		return result;
	}

	getFSData(c: LatLng): Observable<any> {
		const mergeWith = (o1: any, o2: any) => {
			const o: any = {};
			for (const key of [...Object.keys(o1), ...Object.keys(o2)])
				o[key] = o1[key] ? o1[key] : o2[key];
			return o;
		}
		const obs0 = this.getWMSFeatureData(this.fgkMVurl, 'FGK', c)
      .pipe(map(raw => this.getValues(['fa', 'fr', 'fa_text', 'fr_text', 'abt', 'ua', 'tf', 'schl_gis'], raw)));
		const obs1 = this.getWMSFeatureData(this.fsMVurl, 'reviere', c)
      .pipe(map(raw => this.getValues(['fr', 'fa_txt', 'fr_txt'], raw)));
		const obs2 = this.getWMSFeatureData(this.fsMVurl, 'aemter', c)
      .pipe(map(raw => this.getValues(['fa_txt', 'fr_txt'], raw)));
		return combineLatest([obs0, obs1, obs2]).pipe(map(([a, b, c]: any[]) => {
			return mergeWith(a, mergeWith(b, c));
		}));
	}

	getGemarkungenData(c: LatLng): Observable<any> {
		const obs0 = this.getWMSFeatureData(this.gemarkungenMVurl, 'flur', c)
      .pipe(map(raw => this.getValues(['flurname', 'gmknr', 'flurnr'], raw)));
		const obs1 = this.getWMSFeatureData(this.gemarkungenMVurl, 'gema', c)
      .pipe(map(raw => this.getValues(['gmknr', 'gmkname'], raw)));
		const obs2 = this.getWMSFeatureData(this.flurstueckMVurl, 'CP.CadastralParcel', c, 'text/xml;subtype=gml/3.2.1')
      .pipe(map(raw => this.getXMLValues(['cp:label'], raw)));
		return combineLatest([obs0, obs1, obs2]).pipe(map(([a, b, c]: any[]) => {
			return mergeWith(a, mergeWith(b, c));
		}));
	}

	getStandortData(c: LatLng): Observable<any> {
		return this.getWMSFeatureData(this.standortkarteMVurl, 'standortskarten', c)
      .pipe(map(raw => this.getValues(['klimastufe'], raw)));
	}

	/** BB Klimafeuchte (Klimastufe-Äquivalent in BB) */
	getBBStandortData(c: LatLng): Observable<any> {
		return this.getWMSFeatureData(this.standortkarteBBurl, 'stok_kf', c)
			.pipe(map(raw => {
				const v = this.getValues(['klfeu_6190'], raw);
				return { klimastufe: v.klfeu_6190 || '' };
			}));
	}
	
	getWuchsgebietData(c: LatLng) {
		return this.getWMSFeatureData(this.wuchsgebieteD, 'wb_2020', c)
      .pipe(map(raw => this.getValues(['bez_wg_bu', 'bez_wb_bu'], raw)));
	}

	/**
	 * Erkennt das Bundesland anhand der Koordinate.
	 * Bei Überlappung BB/MV (lat 53.35–53.40) wird MV bevorzugt.
	 */
	private getRegion(c: LatLng): 'MV' | 'BB' | null {
		if (c.lat >= 53.35 && c.lat <= 54.68 && c.lng >= 10.59 && c.lng <= 14.41) return 'MV';
		if (c.lat >= 51.35 && c.lat <= 53.40 && c.lng >= 11.26 && c.lng <= 14.77) return 'BB';
		return null;
	}

	/** BB Forstadresse: Forstamt + Revier (Abt/UA/TF nicht verfügbar in BB) */
	getBBFSData(c: LatLng): Observable<any> {
		const obs0 = this.getWMSFeatureData(this.fsBBurl, 'rev', c)
			.pipe(map(raw => {
				const v = this.getValues(['foa_name', 'rev_name'], raw);
				return { fa_txt: v.foa_name, fr_txt: v.rev_name, abt: '', ua: '', schl_gis: '' };
			}));
		const obs1 = this.getWMSFeatureData(this.fsBBurl, 'foa', c)
			.pipe(map(raw => {
				const v = this.getValues(['foa_name'], raw);
				return { fa_txt: v.foa_name, fr_txt: '', abt: '', ua: '', schl_gis: '' };
			}));
		return combineLatest([obs0, obs1]).pipe(map(([a, b]) => mergeWith(a, b)));
	}

	/** BB Katasteradresse via WFS: Gemarkung + Flur + Flurstück in einer Abfrage */
	getBBGemarkungenData(c: LatLng): Observable<any> {
		const tol = 0.0001;
		const bbox = `${c.lat - tol},${c.lng - tol},${c.lat + tol},${c.lng + tol},urn:ogc:def:crs:EPSG::4326`;
		const params = new HttpParams({
			fromObject: {
				SERVICE: 'WFS',
				VERSION: '2.0.0',
				REQUEST: 'GetFeature',
				TYPENAMES: 'ave:Flurstueck',
				COUNT: '1',
				BBOX: bbox,
				OUTPUTFORMAT: 'application/gml+xml; version=3.2'
			}
		});
		return this.http.get(this.katasterBBurl, { params, responseType: 'text' }).pipe(
			map(raw => {
				const v = this.getXMLValues(['gemarkung', 'gemaschl', 'flur', 'flstnrzae', 'flstnrnen'], raw);
				// Flurstück: Zähler/Nenner (z.B. "1711/2" oder nur "1711")
				let flurstueck = v.flstnrzae || '';
				if (v.flstnrnen && v.flstnrnen !== '0' && String(v.flstnrnen).trim()) {
					flurstueck += '/' + v.flstnrnen;
				}
				// Flur: Wert kann "Flur 002" oder "002" sein → Zahl extrahieren
				const flurRaw = String(v.flur || '');
				const flurMatch = flurRaw.match(/(\d+)\s*$/);
				const flurNr = flurMatch ? flurMatch[1].padStart(3, '0') : '';
				return {
					gmkname: v.gemarkung || '',
					gmknr: v.gemaschl || '',
					flurnr: flurNr,
					'cp:label': flurstueck
				};
			})
		);
	}

	/**
	 * Prüft ob WMS-Anfragen aktuell erlaubt sind
	 * Verhindert Massenfehler wenn der Service nicht erreichbar ist
	 */
	private shouldMakeWmsRequest(): boolean {
		if (!this.isOnline) return false;

		// Wenn WMS als nicht verfügbar markiert, prüfe ob Check-Interval abgelaufen
		if (!this.wmsAvailable) {
			const now = Date.now();
			if (now - this.lastWmsCheck < this.WMS_CHECK_INTERVAL) {
				return false; // Noch warten
			}
			// Erlaube einen neuen Versuch
			this.wmsAvailable = true;
		}
		return true;
	}

	/**
	 * Markiert WMS als nicht verfügbar nach einem Fehler
	 */
	private markWmsUnavailable() {
		this.wmsAvailable = false;
		this._wmsUnavailable.set(true);
		this.lastWmsCheck = Date.now();
		// Queue leeren um weitere Anfragen zu verhindern
		const queuedCount = this.requestQueue.length;
		this.requestQueue = [];
		if (queuedCount > 0) {
			console.log(`WMS nicht verfügbar - ${queuedCount} wartende Anfragen verworfen`);
		}
	}

	/**
	 * Führt die nächste Anfrage aus der Queue aus (mit Rate-Limiting)
	 */
	private processQueue(): void {
		if (this.requestQueue.length === 0) return;
		if (this.activeRequests >= this.MAX_CONCURRENT_REQUESTS) return;
		if (!this.wmsAvailable) {
			// Bei nicht verfügbarem WMS: Queue leeren
			this.requestQueue = [];
			return;
		}

		// Rate-Limiting: Mindestabstand zwischen Anfragen
		const now = Date.now();
		const timeSinceLastRequest = now - this.lastRequestTime;
		if (timeSinceLastRequest < this.REQUEST_DELAY_MS) {
			setTimeout(() => this.processQueue(), this.REQUEST_DELAY_MS - timeSinceLastRequest);
			return;
		}

		const nextRequest = this.requestQueue.shift();
		if (nextRequest) {
			this.lastRequestTime = Date.now();
			nextRequest();
		}
	}

	/**
	 * Wrapper für WMS-Subscriptions mit Error-Handling und Rate-Limiting
	 */
	private safeWmsSubscribe<T>(obs: Observable<T>, onSuccess: (data: T) => void) {
		// Request in Queue einreihen (keine Größenbegrenzung - wird langsam abgearbeitet)
		this.requestQueue.push(() => {
			this.activeRequests++;

			obs.pipe(
				timeout(10000), // 10 Sekunden Timeout
				catchError(() => {
					this.markWmsUnavailable();
					return EMPTY;
				})
			).subscribe({
				next: (data) => {
					this.activeRequests--;
					// WMS funktioniert wieder
					this._wmsUnavailable.set(false);
					onSuccess(data);
					// Views benachrichtigen dass WMS-Daten geladen wurden
					this.wmsDataLoaded.next();
					this.processQueue(); // Nächste Anfrage starten
				},
				error: () => {
					this.activeRequests--;
					this.markWmsUnavailable();
					this.processQueue();
				},
				complete: () => {
					// Falls kein next/error kam
					if (this.activeRequests > 0) {
						this.activeRequests--;
						this.processQueue();
					}
				}
			});
		});

		// Queue-Verarbeitung starten
		this.processQueue();
	}

	/**
	 * Befüllt ein Dokument mit WMS-Daten über den injizierten Handler.
	 * Der Handler kapselt die datenmodell-spezifische Logik.
	 *
	 * @param document Das zu befüllende Dokument
	 * @param overwrite Wenn true, werden bestehende Werte überschrieben
	 */
	fetchWmsDataForDocument<T>(document: T, overwrite = false): void {
		if (!this.fieldMappingHandler) {
			console.warn('WmsDataService: Kein WmsFieldMappingHandler registriert');
			return;
		}
		if (!this.shouldMakeWmsRequest()) return;

		// Bei overwrite (z.B. Koordinatenänderung) Query-Status zurücksetzen
		if (overwrite) {
			this.resetQueriedFields();
		}

		const pt = this.fieldMappingHandler.getCoordinates(document);
		const region = this.getRegion(pt);

		// Auto-Save: Nach WMS-Daten debounced in DB speichern
		let saveTimer: any;
		const scheduleSave = () => {
			clearTimeout(saveTimer);
			saveTimer = setTimeout(() => {
				this.fieldMappingHandler?.saveDocument?.(document);
			}, 1500);
		};

		// Klimastufe — region-spezifisch (MV: Standortkarte, BB: Klimafeuchte)
		if ((region === 'MV' || region === 'BB') && (overwrite || this.fieldMappingHandler.shouldFetchKlimastufe(document))) {
			const stObs = region === 'BB' ? this.getBBStandortData(pt) : this.getStandortData(pt);
			this.safeWmsSubscribe(stObs, (data: any) => {
				this.markFieldsQueried(['Klimastufe', 'KlimastufeNeu']);
				if (data) {
					this.fieldMappingHandler!.setKlimastufeData(document, data);
				}
				scheduleSave();
			});
		}

		// Forstadresse — region-spezifisch (MV oder BB)
		if ((region === 'MV' || region === 'BB') && (overwrite || this.fieldMappingHandler.shouldFetchForstadresse(document))) {
			const fsObs = region === 'BB' ? this.getBBFSData(pt) : this.getFSData(pt);
			this.safeWmsSubscribe(fsObs, (data: any) => {
				this.markFieldsQueried(['Forstamt', 'Revier', 'Abteilung', 'Unterabteilung', 'Teilfläche', 'Hilfsfläche']);
				if (data) {
					this.fieldMappingHandler!.setForstadresseData(document, data);
				}
				scheduleSave();
			});
		}

		// Katasteradresse — region-spezifisch (MV oder BB)
		if ((region === 'MV' || region === 'BB') && (overwrite || this.fieldMappingHandler.shouldFetchKatasteradresse(document))) {
			const gkObs = region === 'BB' ? this.getBBGemarkungenData(pt) : this.getGemarkungenData(pt);
			this.safeWmsSubscribe(gkObs, (data: any) => {
				this.markFieldsQueried(['Gemarkung', 'Flur', 'Flurstück']);
				if (data) {
					this.fieldMappingHandler!.setKatasteradresseData(document, data);
				}
				scheduleSave();
			});
		}

		// Wuchsgebiet — deutschlandweit, keine Regionsprüfung
		if (overwrite || this.fieldMappingHandler.shouldFetchWuchsgebiet(document)) {
			this.safeWmsSubscribe(this.getWuchsgebietData(pt), (data: any) => {
				this.markFieldsQueried(['Wuchsgebiet', 'Wuchsbezirk']);
				if (data) {
					this.fieldMappingHandler!.setWuchsgebietData(document, data);
				}
				scheduleSave();
			});
		}
	}
}
